import { $t } from '@/i18n';
/**
 * 合并视图组件
 *
 * 根据数据类型和冲突状态路由到不同的子组件：
 * - 普通 Schema + 无冲突 → CodeConflictResolvedView (Diff 对比)
 * - 普通 Schema + 有冲突 → CodeConflictEditorView (冲突编辑器)
 * - AI 项目 + 无冲突 → AIConflictResolvedView (AIPageDiff 文件级 Diff)
 * - AI 项目 + 有冲突 → AIConflictEditorView (AI 编辑器)
 */
import { defineComponent, computed } from '@vue/composition-api';
import CodeConflictResolvedView from './CodeConflictResolvedView';
import styles from './styles/merge-modal.module.scss';
import CodeConflictEditorView from './CodeConflictEditorView';
import AIConflictEditorView from './AIConflictEditorView';
import AIConflictResolvedView from './AIConflictResolvedView';
import { isAIProjectInfo } from './utils/mergeUtils';
import { yamlToJson } from '@/utils/merge/jsonToYaml';
export default defineComponent({
    name: 'MergeView',
    components: {
        CodeConflictResolvedView,
    },
    props: {
        change: {
            type: Object,
            default: () => ({}),
        },
        currentBranch: {
            type: String,
            default: '',
        },
        mergeBranch: {
            type: String,
            default: '',
        },
    },
    emits: ['change', 'reResolve'],
    setup(props, { emit }) {
        /**
         * 是否可以重新解决冲突
         */
        const canReResolveComputed = computed(() => {
            const item = props.change;
            const isAI = isAIProjectInfo(item.schemaKey);
            return !isAI && item.sectionType === 'resolved' && Boolean(item.wasConflict);
        });
        /**
         * 是否是只读模式
         * 已解决或已合并的都是只读
         */
        const isReadonlyComputed = computed(() => props.change.sectionType === 'resolved' || props.change.sectionType === 'merged');
        /**
         * 是否是 AI 项目信息
         */
        const isAIProject = computed(() => isAIProjectInfo(props.change.schemaKey));
        /**
         * 处理冲突内容变化
         */
        const onChangeConflict = (result) => {
            // 支持两种格式：普通字符串 或 带 resolved 标志的对象
            const isResolved = typeof result === 'object' && result.resolved;
            const data = typeof result === 'object' ? result.data : result;
            emit('change', {
                ...props.change,
                // 如果明确标记为已解决，则更新 conflict 状态
                conflict: isResolved ? false : props.change.conflict,
                content: {
                    ...props.change.content,
                    // 空 data 表示不覆盖 after（等 mergeSuccess 的 projectInfo）
                    ...(data ? { after: data } : {}),
                },
            });
        };
        /**
         * 处理重新解决冲突
         */
        const handleReResolve = () => {
            emit('reResolve', props.change);
        };
        /**
         * 渲染普通场景的对比视图（无冲突或已解决）
         * 用于展示普通 Schema 的 before/after 差异对比
         */
        const renderNormalDiffView = () => (<CodeConflictResolvedView class={styles.editor_diff_detail} type="yaml" originalData={props.change.content.before} modifiedData={props.change.content.after} currentBranch={props.currentBranch} mergeBranch={props.mergeBranch} readonly={isReadonlyComputed.value}/>);
        /**
         * 渲染普通场景的冲突编辑器
         * 用于手动解决普通 Schema 的冲突
         */
        const renderNormalConflictEditor = () => (<CodeConflictEditorView class={styles.editor_diff_detail} onChange={onChangeConflict} conflictItem={props.change.content} key={props.change.key} currentBranchLabel={props.currentBranch} mergeBranchLabel={props.mergeBranch}/>);
        /**
         * 渲染 AI 项目场景的已解决 / 无冲突 Diff 视图
         * 与版本变更一致：用 before/after 的 mainBranch 拉取文件树对比
         */
        const renderAIResolvedView = () => {
            const { before = '', after = '' } = props.change.content || {};
            let originalData = {};
            let modifiedData = {};
            try {
                originalData = (before ? yamlToJson(before) : {});
            }
            catch (e) {
                console.error($t('k_a8a55532'), e);
            }
            try {
                modifiedData = (after ? yamlToJson(after) : {});
            }
            catch (e) {
                console.error($t('k_bf01a9f5'), e);
            }
            return (<AIConflictResolvedView class={styles.editor_diff_detail} originalData={originalData} modifiedData={modifiedData} currentBranch={props.currentBranch} mergeBranch={props.mergeBranch}/>);
        };
        /**
         * 渲染 AI 项目场景的冲突编辑器
         * 提供 AI 辅助的冲突解决界面
         */
        const renderAIConflictEditor = () => (<AIConflictEditorView class={styles.editor_diff_detail} conflictItem={props.change.content} key={props.change.key} currentBranchLabel={props.currentBranch} mergeBranchLabel={props.mergeBranch} onChange={onChangeConflict}/>);
        /**
         * 根据当前状态选择合适的视图进行渲染
         *
         * 渲染策略：
         * | 类型 | 状态 | 视图 |
         * |------|------|------|
         * | AI 项目 | 有冲突 | AIConflictEditorView |
         * | AI 项目 | 无冲突 | AIConflictResolvedView (AIPageDiff) |
         * | 普通 Schema | 有冲突 | CodeConflictEditorView |
         * | 普通 Schema | 无冲突 | CodeConflictResolvedView |
         */
        const renderContentView = () => {
            const hasConflict = props.change.conflict;
            // AI 项目场景
            if (isAIProject.value) {
                return hasConflict
                    ? renderAIConflictEditor()
                    : renderAIResolvedView();
            }
            // 普通 Schema 场景
            return hasConflict
                ? renderNormalConflictEditor()
                : renderNormalDiffView();
        };
        return () => (<div class="relative flex h-full min-h-0 flex-col overflow-hidden">
        {/* 顶部操作栏 - 显示重新解决按钮（仅对已解决的冲突显示） */}
        {canReResolveComputed.value && (<div class={styles.merge_modal_radio_group}>
            <a-button type="link" onClick={handleReResolve}>
              {$t('k_9da91dca')}
            </a-button>
          </div>)}
        {renderContentView()}
      </div>);
    },
});
